import { createContext, useContext, useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";

const AuthContext = createContext(null);

const ROLE_CLAIM =
    "http://schemas.microsoft.com/ws/2008/06/identity/claims/role";

const getStoredToken = () =>
    localStorage.getItem("authToken") ||
    localStorage.getItem("token") ||
    localStorage.getItem("jwt") ||
    sessionStorage.getItem("authToken") ||
    sessionStorage.getItem("token") ||
    null;

const clearAllTokens = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("token");
    localStorage.removeItem("jwt");
    localStorage.removeItem("role");

    sessionStorage.removeItem("authToken");
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("2fa:userId");
};

const buildUserFromToken = (jwt) => {
    const p = jwtDecode(jwt);

    if (p?.exp && Date.now() / 1000 >= p.exp) {
        throw new Error("Token expired");
    }

    const email =
        p.email ||
        p.preferred_username ||
        p.username ||
        p.name ||
        p["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"] ||
        null;

    const rawRole = p.role || p.roles || p[ROLE_CLAIM];

    const role = Array.isArray(rawRole)
        ? rawRole[0]?.toLowerCase()
        : rawRole?.toLowerCase();

    return { email, role: role || "user" };
};

export const AuthProvider = ({ children }) => {
    const [token, setToken] = useState(getStoredToken());
    const [user, setUser] = useState(null);

    useEffect(() => {
        if (!token) {
            setUser(null);
            return;
        }

        try {
            const nextUser = buildUserFromToken(token);
            setUser(nextUser);

            if (nextUser.role) {
                localStorage.setItem("role", nextUser.role);
            }
        } catch {
            clearAllTokens();
            setUser(null);
            setToken(null);
        }
    }, [token]);

    const login = (jwt) => {
        localStorage.setItem("token", jwt);
        localStorage.setItem("authToken", jwt);
        sessionStorage.removeItem("2fa:userId");

        const nextUser = buildUserFromToken(jwt);
        if (nextUser.role) {
            localStorage.setItem("role", nextUser.role);
        }

        setUser(nextUser);
        setToken(jwt);
    };

    const logout = () => {
        clearAllTokens();
        setToken(null);
        setUser(null);
    };

    return (
        <AuthContext.Provider value={{ token, user, login, logout, isAuthenticated: !!token }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);